import { QueryClient } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from './components/utils/theme-provider';
import { TooltipProvider } from './components/ui/tooltip';
import './index.css';
import { routeTree } from './routeTree.gen';
import { AuthProvider, useAuth } from './auth';
import { useToast } from './hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    auth: undefined!,
    toast: undefined!,
    queryClient: undefined!,
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
    },
  },
});
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  serialize: data => {
    // only keep enums data
    data.clientState.queries = data.clientState.queries.filter(query =>
      query.queryKey.includes('game-servers-enum'),
    );
    return JSON.stringify(data);
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
window.process = import.meta as any;

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const auth = useAuth();
  const { toast } = useToast();
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: localStoragePersister }}
    >
      <ThemeProvider>
        <TooltipProvider>
          <RouterProvider router={router} context={{ auth, toast, queryClient }} />
        </TooltipProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <Toaster />
      <AuthProvider>
        <App />
      </AuthProvider>
    </StrictMode>,
  );
}
